import { useState } from 'react';
import type { QuoteDto } from '@/api/generated';
import { useDeleteQuote, useQuotes, useSelectQuote } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatNumberNlBE, formatShortDate, hueForName, toNumber } from '@/lib/format';
import { QuoteCreateDialog, QuoteEditDialog } from './QuoteDialogs';
import styles from './Quotes.module.css';

// 2026-05-17 — the per-line COMPARISON_ROWS + NOTES arrays were Phase-1
// design fixtures (window frames, glass U-w, beslag-garantie etc.) that
// rendered on every quote comparison regardless of the actual quotes the
// user uploaded. The API exposes per-quote totals (Quote entity) but not
// the line-by-line spec, so the static rows lied about every project. Both
// removed; the comparison table now shows just the supplier-level totals
// + score + per-quote actions. Re-introduce when QuoteLine entities land.

export function Quotes() {
  const quotes = useQuotes({ pageSize: 50 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<QuoteDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<QuoteDto | null>(null);
  const selectMutation = useSelectQuote();
  const deleteMutation = useDeleteQuote();

  return (
    <AppFrame>
      <QueryBoundary query={quotes} loading="Offertes laden…">
        {(page) => {
          // Pick the most recent comparison subject (in v1 there's only one).
          const cols = page.items.slice(0, 3);
          const subject = cols[0]?.subject ?? 'Offerte vergelijking';

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">Offerte-vergelijker · {subject}</div>
                  <h1 className={styles.title}>Offertes vergelijken</h1>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <Btn onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Offerte toevoegen
                  </Btn>
                  {cols.length > 0 && (
                    <Btn
                      variant="primary"
                      onClick={() => {
                        // Default-select the highest-scoring quote when no
                        // explicit selection has been made yet.
                        const target =
                          cols.find((c) => c.preferred) ??
                          cols.slice().sort((a, b) => toNumber(b.score) - toNumber(a.score))[0];
                        selectMutation.mutate(target.id);
                      }}
                      disabled={selectMutation.isPending}
                    >
                      <Icon name="check" size={14} /> Selecteer aannemer
                    </Btn>
                  )}
                </div>
              </div>
              <QuoteCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
              {editTarget && (
                <QuoteEditDialog
                  open={editTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                  }}
                  quote={editTarget}
                />
              )}
              <ConfirmDialog
                open={deleteTarget !== null}
                onOpenChange={(o) => {
                  if (!o) setDeleteTarget(null);
                }}
                title={`Offerte van ${deleteTarget?.supplier ?? ''} verwijderen?`}
                description="De offerte wordt definitief uit de vergelijker verwijderd."
                confirmLabel="Verwijder"
                destructive
                pending={deleteMutation.isPending}
                onConfirm={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteMutation.mutateAsync(deleteTarget.id);
                    setDeleteTarget(null);
                  } catch {
                    // toast surfaced by hook
                  }
                }}
              />

              <Card className={styles.banner}>
                <div className="row gap-10">
                  <Icon name="decision" size={16} color="var(--info)" />
                  <div className="flex-1">
                    <div className={styles.bannerTitle}>
                      Beslissing nodig — {cols[0]?.phaseId ?? 'Wwd'} fase
                    </div>
                    <div className={styles.bannerSub}>
                      Vergelijk hieronder de offertes en selecteer de voorkeur.
                    </div>
                  </div>
                  <Pill kind="info">
                    {cols.length} offertes · {priceSpread(cols)}% prijsspreiding
                  </Pill>
                </div>
              </Card>

              {cols.length === 0 ? (
                <Card>
                  <div className="mono c-ink-3" style={{ padding: '24px' }}>
                    Nog geen offertes ontvangen.
                  </div>
                </Card>
              ) : (
                <Card className={styles.compCard}>
                  <table className={styles.compTable}>
                    <thead>
                      <tr className={styles.headRow}>
                        <th className={styles.criteriaHead}>
                          <div className="eyebrow">Criterium</div>
                        </th>
                        {cols.map((c) => (
                          <th
                            key={c.id}
                            className={`${styles.colHead} ${c.preferred ? styles.colHeadPrefer : ''}`}
                          >
                            {c.preferred && <div className={styles.preferAccent} />}
                            <div className="row gap-8">
                              <Avatar name={c.supplier} size={32} hue={hueForName(c.supplier)} />
                              <div>
                                <div className={styles.colName}>{c.supplier}</div>
                                <div className={`mono ${styles.colMeta}`}>
                                  {c.city} · {c.version} · {formatShortDate(c.issuedAt)}
                                </div>
                              </div>
                            </div>
                            {c.preferred && (
                              <div className={styles.preferPill}>
                                <Pill kind="accent">jouw voorkeur</Pill>
                              </div>
                            )}
                            <div className={`row gap-4 ${styles.colActions}`}>
                              {!c.preferred && (
                                <Btn
                                  variant="ghost"
                                  className={styles.colActionBtn}
                                  onClick={() => selectMutation.mutate(c.id)}
                                  disabled={selectMutation.isPending}
                                >
                                  Selecteer
                                </Btn>
                              )}
                              <Btn
                                variant="ghost"
                                className={styles.colActionBtn}
                                onClick={() => setEditTarget(c)}
                                aria-label={`Bewerk offerte ${c.supplier}`}
                              >
                                <Icon name="edit" size={12} />
                              </Btn>
                              <Btn
                                variant="ghost"
                                className={`${styles.colActionBtn} c-bad`}
                                onClick={() => setDeleteTarget(c)}
                                aria-label={`Verwijder offerte ${c.supplier}`}
                              >
                                <Icon name="trash" size={12} />
                              </Btn>
                            </div>
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <td className={styles.totalLabel}>
                          <div className="eyebrow">Totaal incl. BTW</div>
                        </td>
                        {cols.map((c) => (
                          <td
                            key={c.id}
                            className={`${styles.totalCell} ${c.preferred ? styles.totalCellPrefer : ''}`}
                          >
                            <div className={styles.totalAmount}>
                              €{formatNumberNlBE(toNumber(c.total))}
                            </div>
                            <div className={`mono ${styles.totalDelta} ${styles.muted}`}>
                              {deltaText(c, cols)}
                            </div>
                          </td>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* COMPARISON_ROWS + NOTES removed 2026-05-17 — they
                          rendered fake "12 ramen aluminium / Driedubbel
                          glas / Vandeput" rows on every quote regardless
                          of what was uploaded. */}
                      <tr>
                        <td className={styles.scoreLabel}>Jouw score (1–10)</td>
                        {cols.map((c) => (
                          <td
                            key={c.id}
                            className={`${styles.scoreCell} ${c.preferred ? styles.scoreCellPrefer : ''}`}
                          >
                            <div className="row gap-6">
                              <div className={styles.scoreBar}>
                                <div
                                  className={styles.scoreFill}
                                  style={{
                                    width: `${toNumber(c.score) * 10}%`,
                                    background: c.preferred ? 'var(--accent)' : 'var(--ink)',
                                  }}
                                />
                              </div>
                              <span className={`mono ${styles.scoreNum}`}>{toNumber(c.score)}</span>
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </Card>
              )}
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function priceSpread(cols: QuoteDto[]): number {
  if (cols.length < 2) return 0;
  const totals = cols.map((c) => toNumber(c.total));
  const min = Math.min(...totals);
  const max = Math.max(...totals);
  if (min === 0) return 0;
  return Math.round(((max - min) / min) * 100);
}

function deltaText(c: QuoteDto, all: QuoteDto[]): string {
  const totals = all.map((q) => toNumber(q.total));
  const max = Math.max(...totals);
  const min = Math.min(...totals);
  const v = toNumber(c.total);
  if (v === min && v !== max) return `−€${formatNumberNlBE(max - v)} t.o.v. duurste`;
  if (v === max && v !== min) return `+€${formatNumberNlBE(v - min)} t.o.v. goedkoopste`;
  return 'middelweg';
}
